import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { markStyle, useContractorColors } from '../contractors/colors';
import { catalogEditUrl } from '../nav/catalogLink';
import { PRICES_RESOURCE, SUBRENTALS_KEY, type PriceView } from '../subrentals/types';
import { formatPln } from './money';
import '../subrentals/subrentals.css';

/**
 * Who sees „Mają to” (rule 7a): a role that sees the suppliers' catalogs, the supplier and the price.
 */
export const seesSupplierOwners = (me: Me | undefined): boolean =>
  !!me && can(me, PRICES_RESOURCE, 'view') && fieldVisible(me, PRICES_RESOURCE, 'supplierId') && fieldVisible(me, PRICES_RESOURCE, 'priceDayGrosze');

/**
 * „Mają to” (owner 2026-10-23 21:46): the suppliers whose catalog has a position linked to the item, with its price per
 * day and their quantity (when seen too); the supplier's name in its colour (#150) opens its catalog on that position
 * (`?edytuj=<priceId>`). In the item's details (in place of „Ceny alternatywne”, owner 2026-10-24 11:24) and in its edit
 * form (owner 2026-10-24 10:55: the owner opens his item there). Null: not allowed (nothing asked) or nothing to show.
 */
export function useSupplierOwners(itemId: string | undefined, show: boolean): ReactNode | null {
  const colors = useContractorColors();
  const enabled = show && !!itemId;
  const prices = useQuery({
    queryKey: [...SUBRENTALS_KEY, 'prices', 'item', itemId ?? ''],
    enabled,
    queryFn: () => api<{ prices?: PriceView[] }>(`/api/subrentals/prices?itemId=${encodeURIComponent(itemId!)}`),
  });
  const list = (prices.data?.prices ?? []).filter((p) => p.supplierId && p.priceDayGrosze !== undefined);
  if (!enabled || list.length === 0) return null;
  return (
    <ul className="supplier-owners" aria-label="Mają to">
      {list.map((p) => (
        <li key={p.id}>
          <Link className="supplier-owner" style={markStyle(colors.get(p.supplierId!))} to={catalogEditUrl({ kind: 'supplier', supplierId: p.supplierId!, priceId: p.id })}>
            {p.supplierName}
          </Link>{' '}
          — {formatPln(p.priceDayGrosze!)}/dzień
          {p.quantity != null && ` (ilość: ${p.quantity})`}
        </li>
      ))}
    </ul>
  );
}
